import { useEffect, useState } from 'react';
import { useSnackbar, closeSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import type { SnackbarKey } from 'notistack';

import CircularProgress from '@mui/material/CircularProgress';

import { PATH_HOME } from '../../app/constants/paths.constant';

import { NotiStackButton } from '../NotiStackButton';
import { LinearProgress } from '../LinearProgress';

import { Wrapper } from './Wrapper';

interface Props {
  readonly notificationDelay?: number;
  readonly notificationMessage?: string;
  readonly showBackdrop?: boolean;
  readonly showNotification?: boolean;
  readonly variant?: 'circular' | 'linear';
}

export const Loading = ({
  notificationDelay = 5000,
  notificationMessage = 'Le chargement prend plus de temps que prévu',
  showBackdrop = true,
  showNotification = true,
  variant = 'circular',
}: Readonly<Props>) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let snackbarKey: SnackbarKey | null = null;

    const snackbarActions = (snackbarKey: SnackbarKey) => {
      const dismiss = () => {
        setDismissed(true);
        closeSnackbar(snackbarKey);
      };

      const closeAndRedirect = () => {
        void navigate(PATH_HOME);
        closeSnackbar(snackbarKey);
      };

      return (
        <>
          {/* eslint-disable-next-line react/jsx-no-bind */}
          <NotiStackButton onClick={closeAndRedirect} sx={{ mr: 2 }}>
            Accueil
          </NotiStackButton>
          {/* eslint-disable-next-line react/jsx-no-bind */}
          <NotiStackButton onClick={dismiss}>Fermer</NotiStackButton>
        </>
      );
    };

    const timer = setTimeout(() => {
      if (!showNotification) {
        return;
      }

      snackbarKey = enqueueSnackbar(notificationMessage, {
        preventDuplicate: true,
        variant: 'warning',
        persist: true,
        action: snackbarActions,
      });
    }, notificationDelay);

    return () => {
      clearTimeout(timer);

      if (!snackbarKey) {
        return;
      }

      closeSnackbar(snackbarKey);
    };
  }, [
    enqueueSnackbar,
    navigate,
    notificationDelay,
    notificationMessage,
    showNotification,
  ]);

  const enableBackdrop = showBackdrop && !dismissed;
  const showCircular = variant === 'circular' && !dismissed;

  return (
    <Wrapper enableBackdrop={enableBackdrop}>
      {showCircular ? <CircularProgress color="inherit" /> : <LinearProgress />}
    </Wrapper>
  );
};
